// src/pages/SuperAdmin/SuperAdminDashboard.jsx
import React from 'react';
import { Row, Col, Card, CardBody, Table } from 'reactstrap';
import { Link } from 'react-router-dom';

const SuperAdminDashboard = ({ stats, loading, setActiveTab }) => {
  const overview = stats?.overview || {};
  const recentClinics = stats?.recentClinics || [];

  return (
    <div>
      {/* Top Metric Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3 mb-xl-0">
          <div className="sa-stat-card">
            <div className="stat-icon-wrapper gold">
              <i className="bx bxs-business" />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Tenants (Clinics)</div>
              <div className="stat-value">{loading ? '...' : (overview.totalClinics || 0)}</div>
              <div className="stat-sub text-success">
                <i className="bx bx-check-circle me-1" />
                {overview.activeSubscriptions || 0} Active Paid &bull; {overview.trialSubscriptions || 0} Trials
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} md={6} className="mb-3 mb-xl-0">
          <div className="sa-stat-card">
            <div className="stat-icon-wrapper emerald">
              <i className="bx bx-rupee" />
            </div>
            <div className="stat-info">
              <div className="stat-label">Monthly Recurring Revenue (MRR)</div>
              <div className="stat-value">₹{(overview.mrr || 0).toLocaleString()}</div>
              <div className="stat-sub text-muted">
                Total Lifetime: ₹{(overview.totalRevenue || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} md={6} className="mb-3 mb-xl-0">
          <div className="sa-stat-card">
            <div className="stat-icon-wrapper indigo">
              <i className="bx bx-user-pin" />
            </div>
            <div className="stat-info">
              <div className="stat-label">Doctors & Specialists</div>
              <div className="stat-value">{loading ? '...' : (overview.totalDoctors || 0)}</div>
              <div className="stat-sub text-muted">
                {overview.totalChairs || 0} Operating Dental Chairs
              </div>
            </div>
          </div>
        </Col>

        <Col xl={3} md={6}>
          <div className="sa-stat-card">
            <div className="stat-icon-wrapper sky">
              <i className="bx bx-group" />
            </div>
            <div className="stat-info">
              <div className="stat-label">Total Registered Patients</div>
              <div className="stat-value">{loading ? '...' : (overview.totalPatients || 0).toLocaleString()}</div>
              <div className="stat-sub text-muted">Across all dental clinics</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Subscription Breakdown & Quick Actions */}
      <Row className="mb-4">
        <Col lg={8}>
          <div className="sa-card">
            <div className="sa-card-header">
              <h5 className="sa-card-title">
                <i className="bx bx-buildings text-warning" /> Recently Registered Dental Clinics
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-outline-dark"
                onClick={() => setActiveTab('tenants')}
              >
                View All Tenants &rarr;
              </button>
            </div>
            <div className="sa-card-body p-0">
              <div className="table-responsive">
                <Table className="table align-middle table-nowrap table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Clinic Name</th>
                      <th>Owner / Doctor</th>
                      <th>Location</th>
                      <th>Plan Status</th>
                      <th>Created Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentClinics.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4 text-muted">
                          {loading ? 'Loading clinics...' : 'No clinics registered yet'}
                        </td>
                      </tr>
                    ) : (
                      recentClinics.map((clinic) => {
                        const sub = clinic.subscriptions?.[0];
                        const planName = sub?.plan?.name || (sub?.status === 'trial' ? 'Free Trial' : 'None');
                        const isTrial = sub?.status === 'trial';
                        const isActive = sub?.status === 'active';

                        return (
                          <tr key={clinic.id}>
                            <td>
                              <div className="font-weight-semibold text-dark">{clinic.clinic_name}</div>
                              <small className="text-muted">{clinic.email || 'No email'}</small>
                            </td>
                            <td>
                              <div>{clinic.client?.name || 'Admin'}</div>
                              <small className="text-muted">{clinic.phone || '-'}</small>
                            </td>
                            <td>
                              <div>{clinic.city || '-'}{clinic.state ? `, ${clinic.state}` : ''}</div>
                            </td>
                            <td>
                              <span className={`status-badge ${isActive ? 'active' : isTrial ? 'trial' : 'expired'}`}>
                                <i className={`bx ${isActive ? 'bx-check-circle' : isTrial ? 'bx-time-five' : 'bx-error-circle'}`} />
                                {planName} ({sub?.status || 'trial'})
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {new Date(clinic.created_at).toLocaleDateString()}
                              </small>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-xs btn-outline-primary"
                                onClick={() => setActiveTab('tenants')}
                              >
                                Manage
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </Col>

        <Col lg={4}>
          <div className="sa-card">
            <div className="sa-card-header">
              <h5 className="sa-card-title">
                <i className="bx bx-pie-chart-alt text-primary" /> Tenant Subscriptions
              </h5>
            </div>
            <div className="sa-card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded">
                <span className="font-weight-medium text-success">
                  <i className="bx bxs-circle font-size-10 me-1" /> Active Paid Subscriptions
                </span>
                <strong className="text-dark">{overview.activeSubscriptions || 0}</strong>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded">
                <span className="font-weight-medium text-warning">
                  <i className="bx bxs-circle font-size-10 me-1" /> In 14-Day Free Trial
                </span>
                <strong className="text-dark">{overview.trialSubscriptions || 0}</strong>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-4 p-2 bg-light rounded">
                <span className="font-weight-medium text-danger">
                  <i className="bx bxs-circle font-size-10 me-1" /> Expired / Past Due
                </span>
                <strong className="text-dark">{overview.expiredSubscriptions || 0}</strong>
              </div>

              <div className="border-top pt-3">
                <div className="font-weight-semibold font-size-13 text-muted mb-2">Platform Quick Shortcuts:</div>
                <div className="d-grid gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => setActiveTab('tenants')}
                  >
                    <i className="bx bx-plus-circle me-1" /> Add New Clinic Tenant
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark"
                    onClick={() => setActiveTab('plans')}
                  >
                    <i className="bx bx-slider-alt me-1" /> Configure Pricing & Features
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SuperAdminDashboard;
